@let c = change();
<header class="aud-head">
  @if (c) {
    <span class="aud-badge aud-badge--op-{{ c.changeType }}">{{ opKey(c.changeType) | translate }}</span>
    <h2 class="aud-title" [title]="c.tableName ?? ''">
      <i class="fa-solid fa-table" aria-hidden="true"></i>
      {{ c.tableLabel || c.tableName || ('audit.change.unknownTable' | translate: { id: c.tableId }) }}
    </h2>
  }
  <button type="button" class="aud-icon-btn aud-close" (click)="closed.emit()" [attr.aria-label]="'audit.actions.close' | translate">
    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
  </button>
</header>

@if (loading() && !c) {
  <p class="aud-state"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'audit.state.loading' | translate }}</p>
} @else if (error() && !c) {
  <p class="aud-state aud-state--error">
    <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ error() }}
    <button type="button" class="aud-link-btn" (click)="retry.emit()">{{ 'audit.actions.retry' | translate }}</button>
  </p>
} @else if (c) {
  <div class="aud-body">
    <section class="aud-record">
      <div>
        <strong>{{ c.recordName || record(c.recordId) }}</strong>
        @if (c.recordName) {
          <small class="aud-muted"><code>{{ record(c.recordId) }}</code></small>
        }
      </div>
      @if (c.recordKey && c.changeType !== 'D' && c.tableName) {
        <button type="button" class="aud-btn aud-btn--primary" (click)="openRecord.emit(c)">
          <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>{{ 'audit.change.openRecord' | translate }}
        </button>
      } @else if (c.changeType === 'D') {
        <span class="aud-muted">{{ 'audit.change.deleted' | translate }}</span>
      }
    </section>

    <dl class="aud-meta">
      <div>
        <dt>{{ 'audit.change.date' | translate }}</dt>
        <dd [title]="fmt().exact(c.created)">{{ fmt().dateTime(c.created) }}</dd>
      </div>
      <div>
        <dt>{{ 'audit.change.user' | translate }}</dt>
        <dd>{{ c.userName || ('audit.change.unknownUser' | translate: { id: c.userId }) }} @if (c.roleName) {<small class="aud-muted">{{ c.roleName }}</small>}</dd>
      </div>
      <div>
        <dt>{{ 'audit.change.session' | translate }}</dt>
        <dd><code>{{ c.sessionId || '-' }}</code></dd>
      </div>
      @if (c.trxName) {
        <div>
          <dt>{{ 'audit.change.transaction' | translate }}</dt>
          <dd><code class="aud-wrap">{{ c.trxName }}</code></dd>
        </div>
      }
    </dl>

    <div class="aud-links">
      @if (c.recordId) {
        <button type="button" class="aud-link-btn" (click)="filterRecord.emit(c)">
          <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>{{ 'audit.change.recordHistory' | translate }}
        </button>
      }
      @if (c.sessionId > 0) {
        <button type="button" class="aud-link-btn" (click)="filterSession.emit(c)">
          <i class="fa-solid fa-link" aria-hidden="true"></i>{{ 'audit.change.sameSession' | translate }}
        </button>
      }
      @if (c.userId > 0) {
        <button type="button" class="aud-link-btn" (click)="filterUser.emit(c)">
          <i class="fa-solid fa-user" aria-hidden="true"></i>{{ 'audit.change.sameUser' | translate }}
        </button>
      }
    </div>

    @if (c.legacy) {
      <p class="aud-note">{{ 'audit.change.legacy' | translate }}</p>
    }

    <section class="aud-section">
      <h3>{{ 'audit.change.fields' | translate }} <span class="aud-count">{{ c.changeCount }}</span></h3>
      @if (c.fields.length === 0) {
        <p class="aud-muted">{{ 'audit.change.noFields' | translate }}</p>
      } @else {
        <table class="aud-diff">
          <thead>
            <tr>
              <th scope="col">{{ 'audit.change.field' | translate }}</th>
              @if (c.changeType !== 'I') {
                <th scope="col">{{ 'audit.change.before' | translate }}</th>
              }
              @if (c.changeType !== 'D') {
                <th scope="col">{{ 'audit.change.after' | translate }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (f of c.fields; track $index) {
              <tr>
                <th scope="row">
                  @if (f.kind === 'note') {
                    {{ 'audit.change.studioNote' | translate }}
                  } @else {
                    {{ f.label || f.columnName || ('#' + f.columnId) }}
                    @if (f.columnName && f.label !== f.columnName) {
                      <small class="aud-muted"><code>{{ f.columnName }}</code></small>
                    }
                  }
                </th>
                @if (c.changeType !== 'I') {
                  <td class="aud-old">
                    <ng-container *ngTemplateOutlet="value; context: { $implicit: f, v: f.oldValue, raw: f.oldRaw }" />
                  </td>
                }
                @if (c.changeType !== 'D') {
                  <td class="aud-new">
                    <ng-container *ngTemplateOutlet="value; context: { $implicit: f, v: f.newValue, raw: f.newRaw }" />
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
        @if (c.changeCount > c.fields.length) {
          <p class="aud-note">{{ 'audit.change.moreFields' | translate: { count: c.changeCount - c.fields.length } }}</p>
        }
      }
    </section>
  </div>
}

<ng-template #value let-f let-v="v" let-raw="raw">
  @let json = f.kind === 'json' ? pretty(v) : null;
  @let label = boolLabel(f, v);
  @if (f.masked) {
    <span class="aud-masked"><i class="fa-solid fa-lock" aria-hidden="true"></i>{{ 'audit.masked' | translate }}</span>
  } @else if (v === null || v === undefined || v === '') {
    <span class="aud-empty">{{ 'audit.change.empty' | translate }}</span>
  } @else if (json) {
    <pre class="aud-pre">{{ json }}</pre>
  } @else if (label) {
    <span class="aud-value">{{ label | translate }}</span>
  } @else {
    <span class="aud-value" [class.aud-value--long]="v.length > 120">{{ display(f, v) }}</span>
    @if (raw && raw !== v) {
      <small class="aud-muted"><code>{{ raw }}</code></small>
    }
  }
</ng-template>
